import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Full-screen intro loader shown once per browser session on the home page.
// Reflects the vaporwave hero background with the VP logo drawing in.
export function LogoLoader({ bg }) {
  const [done, setDone] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("vice_loaded") === "1"
  );

  useEffect(() => {
    if (done) return;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => {
      sessionStorage.setItem("vice_loaded", "1");
      setDone(true);
    }, 2100);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = "";
    };
  }, [done]);

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="vice-loader"
          data-testid="logo-loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
          onAnimationComplete={() => { document.body.style.overflow = ""; }}
          className="fixed inset-0 z-[200] flex flex-col items-center justify-center overflow-hidden bg-void"
        >
          {/* Vaporwave backdrop (same as hero) */}
          {bg && (
            <motion.img
              src={bg}
              alt=""
              aria-hidden="true"
              initial={{ scale: 1.25, opacity: 0 }}
              animate={{ scale: 1.1, opacity: 0.55 }}
              transition={{ duration: 2.1, ease: "easeOut" }}
              className="absolute inset-0 w-full h-full object-cover blur-2xl"
            />
          )}
          <div className="absolute inset-0 bg-void/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-void via-transparent to-void" />

          {/* Logo */}
          <motion.img
            src="/vp-logo-neon.png"
            alt="Vice Performance"
            initial={{ scale: 0.55, opacity: 0, filter: "blur(8px)" }}
            animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 h-24 md:h-36 w-auto"
            style={{ filter: "drop-shadow(0 0 28px rgba(152,255,86,0.55))" }}
          />
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="relative z-10 mt-6 font-display uppercase text-xl md:text-2xl tracking-tight text-[#FAFAFA]"
          >
            Vice<span className="text-neon">Performance</span>
          </motion.div>

          {/* Progress bar */}
          <div className="relative z-10 mt-8 h-[3px] w-40 md:w-52 bg-[#1c1c1c] overflow-hidden">
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: "0%" }}
              transition={{ duration: 2, ease: "easeInOut" }}
              className="h-full w-full bg-neon"
            />
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="relative z-10 mt-4 font-mono text-[10px] uppercase tracking-[0.3em] text-[#9CA3AF]"
          >
            Spooling up
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
